import { useEffect, useMemo } from "react";

import { type ProfileResponse } from "@vega-api/generated/types/profileResponse";
import { LocalStorageKeys } from "@vega-core/constants/cookies";
import { useLocaleStorage } from "@vega-core/hooks/useLocaleStorage";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { getAppConfig } from "@vega-core/utils/appConfig";
import { analytics } from "@vega-core/utils/posthog/analytics";

import { SessionContext, type SessionContextInterface } from "./SessionContext";

export const SessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useLocaleStorage<ProfileResponse | null>(
    LocalStorageKeys.User,
    null,
  );
  const {
    global: { companyId, companyName },
  } = usePublicConfig();

  useEffect(() => {
    analytics.register({
      portal_type: import.meta.env.VITE_PORTAL_TYPE,
      environment: getAppConfig().mode,
      company_id: companyId,
      company_name: companyName,
    });
  }, [companyId, companyName]);

  const userId = user?.id;
  useEffect(() => {
    if (userId != null && userId !== "") analytics.identify(userId);
  }, [userId]);

  const value: SessionContextInterface = useMemo(
    () => ({
      clearUser: () => {
        analytics.reset();
        setUser(null);
      },
      setUserData: (updatedUser: SessionContextInterface["user"]) => {
        setUser(updatedUser);
      },
      user,
    }),
    [user, setUser],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
